import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'

import NetworkService from '@/Network'
import type { StationLegLock, StationRegistration } from '@/Network/Services/StationService'

/**
 * Trava por perna do plantão (backend #3055): check-in ou check-out registrado
 * pelo app ou validado na auditoria impede alterar horário/médico/cobertura/
 * valor/tipo/time e excluir. Setor e especialidade continuam livres.
 *
 * O web NÃO reimplementa a regra: só lê o `legLock` de
 * `GET /stations/:doctorStationId/registration` para DESABILITAR e EXPLICAR as
 * ações. A trava real é o backend (400 `DOCTOR_STATION_ALREADY_HAS_CHECK_IN`).
 */

/** Só quando o backend trava sem mandar motivo — o tooltip nunca fica vazio. */
export const STATION_LEG_LOCK_FALLBACK_REASON =
  'Plantão com check-in ou check-out registrado pelo aplicativo ou validado na auditoria: ' +
  'não pode ser alterado nem excluído.'

const UNLOCKED: StationLegLock = { locked: false, reason: null }

/**
 * Trava efetiva a partir de um registro já carregado. Registro ausente (rota
 * indisponível, sem permissão de leitura, backend antigo) = destravado.
 */
export function resolveStationLegLock(
  registration: Pick<StationRegistration, 'legLock'> | null | undefined
): StationLegLock {
  const legLock = registration?.legLock
  if (!legLock?.locked) return UNLOCKED
  return { locked: true, reason: legLock.reason || STATION_LEG_LOCK_FALLBACK_REASON }
}

/**
 * Para quem NÃO tem o registro em mãos (ex.: `update.station.modal`, aberto do
 * Dashboard, do IndexV2 e do histórico de troca): busca pelo método de rede que
 * já existe, sem endpoint novo.
 */
export function useStationLegLock(doctorStationId: MaybeRefOrGetter<number | null | undefined>) {
  const legLock = ref<StationLegLock>(UNLOCKED)
  // Descarta resposta atrasada de um plantão anterior (modal reaproveitado).
  let requestSeq = 0

  async function refresh(): Promise<void> {
    const seq = ++requestSeq
    const id = Number(toValue(doctorStationId))
    if (!Number.isFinite(id) || id <= 0) {
      legLock.value = UNLOCKED
      return
    }
    try {
      const registration = await NetworkService.station.getStationRegistration(id)
      if (seq === requestSeq) legLock.value = resolveStationLegLock(registration)
    } catch (error) {
      // fallback intencional: sem o registro a tela segue destravada e o backend
      // recusa a mutação travada com a frase dele (toast via `getStationCheckInLockMessage`).
      console.debug('[StationLegLock] registro indisponível; ações seguem habilitadas', error)
      if (seq === requestSeq) legLock.value = UNLOCKED
    }
  }

  const isLegLocked = computed(() => legLock.value.locked)
  const legLockReason = computed(() => legLock.value.reason)

  return { legLock, isLegLocked, legLockReason, refresh }
}
